(() => {
  'use strict';
  const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const matches = (card, tag, needles) => {
    if (!tag && !needles.length) return true;
    const tags = (card.dataset.ggpsTags || '').split('|');
    if (tag && tags.includes(tag)) return true;
    if (tag && tags.some(value => value.startsWith('artist:'))) return false;
    return needles.some(value => (card.dataset.ggpsKeywords || '').includes(normalize(value)));
  };

  async function getProducts(path, tag, needles, limit) {
    const cards = [], ids = new Set(), visited = new Set();
    let next = new URL(path, location.origin);
    next.searchParams.set('filter.v.availability', '1');
    next.searchParams.set('page', '1');
    for (let count = 0; next && count < 20 && cards.length < limit; count++) {
      if (next.origin !== location.origin) throw new Error('Unexpected product feed origin');
      next.searchParams.set('filter.v.availability', '1');
      next.searchParams.set('sort_by', 'created-desc');
      next.searchParams.set('section_id', 'gg-contextual-product-feed');
      const url = next.toString();
      if (visited.has(url)) break;
      visited.add(url);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      let response;
      try {
        response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Product feed unavailable');
        const html = await response.text();
        const feed = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ggps-feed]');
        if (!feed) throw new Error('Product feed missing');
        feed.querySelectorAll('[data-ggps-product]').forEach(card => {
          const id = card.dataset.ggpsProduct;
          if (cards.length < limit && !ids.has(id) && matches(card, tag, needles)) {
            ids.add(id);
            cards.push(card);
          }
        });
        next = feed.dataset.next ? new URL(feed.dataset.next, next) : null;
      } finally {
        clearTimeout(timer);
      }
    }
    return cards;
  }

  function initialize(root) {
    if (root.dataset.ggpsReady) return;
    root.dataset.ggpsReady = 'true';
    const productLimit = Math.min(12, Math.max(1, Number(root.dataset.limit) || 12));
    const topLinkTemplate = root.querySelector('[data-ggps-top-link]');
    const heading = document.querySelector('#MainContent h1');
    if (topLinkTemplate && heading && !document.querySelector('[data-ggps-top-shop]')) {
      const link = topLinkTemplate.content.firstElementChild.cloneNode(true);
      const introduction = heading.nextElementSibling;
      if (introduction && (introduction.tagName === 'P' || introduction.matches('[class*="subtitle"],[class*="description"],[class*="lede"]'))) introduction.after(link);
      else heading.after(link);
    }
    const legacyModules = [...document.querySelectorAll('.gg-invslider, .gg-pageshop, .gg-artshop')];
    if (legacyModules.length) {
      const firstLegacy = legacyModules[0];
      if (root.dataset.placement !== 'article_end') firstLegacy.before(root);
      legacyModules.forEach(legacy => {
        legacy.querySelectorAll('[data-gg-dropalert]').forEach(alert => root.querySelector('.ggps-wrap').append(alert));
        const wrapper = legacy.parentElement;
        legacy.remove();
        if (wrapper.classList.contains('shopify-section') && !wrapper.querySelector('h1,h2,h3,p,a,img,video,form')) {
          wrapper.hidden = true;
          wrapper.style.setProperty('display', 'none', 'important');
        }
      });
    }
    const placementAnchor = document.querySelector('[data-ggps-after]');
    if (placementAnchor) placementAnchor.after(root);
    const track = root.querySelector('.ggps-track');
    const previous = root.querySelector('[data-ggps-prev]');
    const next = root.querySelector('[data-ggps-next]');
    const controls = root.querySelector('.ggps-controls');
    const status = root.querySelector('.ggps-status');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let interacted = false;
    const markInteracted = () => { interacted = true; };
    ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach(type => track.addEventListener(type, markInteracted, { passive: true }));
    controls.addEventListener('focusin', markInteracted);

    function update() {
      const items = [...track.children];
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = Math.abs(track.scrollLeft);
      const startPadding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      previous.disabled = position <= startPadding + 2;
      next.disabled = position >= max - 2;
      controls.hidden = max < 2;
      const bounds = track.getBoundingClientRect();
      const visible = items.map((item, index) => ({ bounds: item.getBoundingClientRect(), index })).filter(item => item.bounds.right > bounds.left + 8 && item.bounds.left < bounds.right - 8);
      status.textContent = visible.length ? `Products ${visible[0].index + 1}–${visible[visible.length - 1].index + 1} of ${items.length}` : '';
    }
    const move = direction => {
      interacted = true;
      const first = track.firstElementChild;
      if (!first) return;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      track.scrollBy({ left: direction * step, behavior: reduced.matches ? 'auto' : 'smooth' });
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        interacted = true;
        track.scrollTo({ left: event.key === 'Home' ? 0 : track.scrollWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
      }
    });
    let scheduled = false;
    track.addEventListener('scroll', () => {
      if (!scheduled) requestAnimationFrame(() => { update(); scheduled = false; });
      scheduled = true;
    }, { passive: true });
    if (window.ResizeObserver) new ResizeObserver(update).observe(track);
    else window.addEventListener('resize', update, { passive: true });
    update();

    async function load() {
      root.dataset.ggpsLoading = 'true';
      const choices = [{ url: root.dataset.primary, label: root.dataset.subject, tag: root.dataset.requiredTag, needles: (root.dataset.needles || '').split('|').filter(Boolean) }];
      root.querySelector('[data-ggps-fallbacks]').content.querySelectorAll('[data-url]').forEach(item => choices.push({ url: item.dataset.url, label: item.dataset.label, tag: '', needles: [] }));
      const tried = new Set();
      let completed = false;
      for (const choice of choices) {
        const key = `${choice.url}|${choice.tag}|${choice.needles.join('|')}`;
        if (tried.has(key)) continue;
        tried.add(key);
        try {
          const cards = await getProducts(choice.url, choice.tag, choice.needles, productLimit);
          if (!cards.length) continue;
          if (!interacted || !track.children.length) {
            track.replaceChildren(...cards);
            track.scrollLeft = 0;
            root.querySelector('.ggps-heading').textContent = choice.label ? `Shop ${choice.label}` : 'Available at Gauntlet Gallery';
            const link = root.querySelector('[data-ggps-all]');
            const collectionURL = new URL(choice.url, location.origin);
            collectionURL.searchParams.set('filter.v.availability', '1');
            collectionURL.searchParams.set('sort_by', 'created-desc');
            link.href = collectionURL.toString();
            root.dataset.source = collectionURL.pathname.split('/').filter(Boolean).pop();
            root.querySelector('.ggps-empty').hidden = true;
          }
          root.dataset.ggpsLoaded = 'true';
          completed = true;
          break;
        } catch (error) {
          // Existing server-rendered cards remain usable if the feed is unavailable.
        }
      }
      if (!completed && !track.children.length) {
        root.querySelector('.ggps-heading').textContent = 'Explore Gauntlet Gallery';
        root.querySelector('.ggps-empty').hidden = false;
      }
      root.dataset.ggpsLoading = 'false';
      update();
    }
    if (window.IntersectionObserver) {
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); load(); }
      }, { rootMargin: '600px' });
      observer.observe(root);
    } else load();
  }
  function start() { document.querySelectorAll('[data-ggps]').forEach(initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
